import { AppNS } from 'app';
import { useCompaniesStore } from 'app/core/store_v2/companies';
import CheckSuccessIcon from 'app/ui_v2/icons/CheckSuccessIcon';
import { Link } from 'react-router-dom';

import { Box, Button, SxProps, Theme, useTheme } from '@mui/material';

import {
  isCompanyHasAcknowledgementsAccepted,
  isCompanyHasContactInfo,
  isCompanyHasDisciplines,
  isCompanyHasPriceInfo,
  isCompanyHasVerifiedMerchantAccount,
} from '../../publish-profile/helpers';
import { isFieldsExcluded } from '../../qualifications-sections/sections/section-validation/SectionValidation';

export interface AgencyProfileProgressListProps {
  companyData: AppNS.Company;
}

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    list: {
      listStyleType: 'none',
      margin: '16px 0 0 0',
      padding: 0,
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    emptyCircle: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      border: '2px solid #fff',
      display: 'inline-block',
      marginLeft: '1px',
      marginRight: '3px',
      flexShrink: 0,
    },
    button: {
      padding: 0,
      minWidth: 'unset',
      color: '#fff !important',
      marginLeft: theme.spacing(1),
    },
  };
};

export default function AgencyProfileProgressList(props: AgencyProfileProgressListProps) {
  const { companyData } = props;

  const { isPricingPolicyRuleNeededToPublish } = useCompaniesStore();

  const theme = useTheme();
  const styles = getStyles(theme);

  const data = [
    {
      label: 'Add agency contact info',
      link: `/agency/${companyData.id}/main#contact-info-section`,
      tabName: 'General info',
      isPassed: isCompanyHasContactInfo(companyData),
    },
    {
      label: 'Select at least one discipline',
      link: `/agency/${companyData.id}/disciplines#agency-disciplines-section`,
      tabName: 'Disciplines',
      isPassed: isCompanyHasDisciplines(companyData),
    },
    {
      label: 'Accept all acknowledgements',
      link: `/agency/${companyData.id}/disciplines#acknowledgements-section`,
      tabName: 'Disciplines',
      isPassed: isCompanyHasAcknowledgementsAccepted(companyData),
    },
    {
      label: 'Add and verify Payout Info',
      link: `/agency/${companyData.id}/payout-setup`,
      tabName: 'Payout Setup',
      isPassed: isCompanyHasVerifiedMerchantAccount(companyData),
    },
  ];

  const disciplinesNames: AppNS.DisciplineId[] = companyData.disciplines?.map((d) => d.name as AppNS.DisciplineId);

  if (isPricingPolicyRuleNeededToPublish || !isFieldsExcluded(disciplinesNames || [])) {
    data.push({
      label: 'Add agency pricing info',
      link: `/agency/${companyData.id}/disciplines#pricing-policy-section`,
      tabName: 'Disciplines',
      isPassed: isCompanyHasPriceInfo(companyData),
    });
  }

  return (
    <Box component="ul" sx={styles.list}>
      {data.map((item, index) => (
        <Box component="li" key={index} sx={styles.listItem}>
          {item.isPassed ? <CheckSuccessIcon /> : <Box sx={styles.emptyCircle}></Box>}
          <div>
            <strong>{item.label}</strong>
            <Button variant="text" color="primary" sx={styles.button} component={Link} to={item.link}>
              ({item.tabName} tab)
            </Button>
          </div>
        </Box>
      ))}
    </Box>
  );
}
